import React, { useState } from "react";
import { useSubscriptionStore, useUserStore } from "../../state";
import { useSubUserStore } from "../../state/useSubUserStore";
import { getCurrentMonthData } from "../../utils/DateTimeRange";
import { useCompanyStore } from "../../state/useCompanyStore";

interface ShowFeatures {
  showBusinessType: boolean;
  showCountry: boolean;
  showBusinessName: boolean;
}

export const useUserDataLoader = () => {
  const { setUser } = useUserStore();
  const {
    fetchCompanyProfile,
    setBudget,
    setCompanyBankAccounts,
    setCompany,
    company,
  } = useCompanyStore();
  const fetchActiveSubscription = useSubscriptionStore(
    (state) => state.fetchActiveSubscription,
  );
  const fetchUser = useSubUserStore((state) => state.fetchUser);
  const { setSubscriptionData } = useSubscriptionStore();
  const [loading, setLoading] = useState(false);
  const [isPasswordModalShown, setIsPasswordModalShown] = useState(false);
  const [businessModalVisible, setBusinessModalVisible] = useState(false);
  const [error, setError] = useState<string | null>();
  const [showFeatures, setShowFeatures] = useState({
    showBusinessType: false,
    showCountry: false,
    showBusinessName: false,
  });

  const determineModalVisibility = (
    companyProfile: any,
    activeSubscription: any,
    userId: string,
  ) => {
    const { isPasswordChanged, isRootUser } = useSubUserStore.getState();

    if (!isPasswordChanged && !isRootUser) {
      setIsPasswordModalShown(true);
    } else {
      setIsPasswordModalShown(false);
    }
    // Determine validity for each condition and update showFeatures state
    const updatedShowFeatures = {
      showBusinessType:
        !companyProfile.businessType ||
        companyProfile.businessType.trim() === "",
      showCountry:
        !companyProfile.country || companyProfile.country.trim() === "",
      showBusinessName:
        !companyProfile.businessName ||
        companyProfile.businessName.trim() === "",
    };

    setShowFeatures(updatedShowFeatures);

    // Update modal visibility based on conditions
    setBusinessModalVisible(
      updatedShowFeatures.showBusinessType ||
        updatedShowFeatures.showCountry ||
        updatedShowFeatures.showBusinessName,
    );
  };

  const fetchData = async (userId: string) => {
    setLoading(true);
    try {
      const [companyProfile, subUser, activeSubscription] = await Promise.all([
        fetchCompanyProfile(),
        fetchUser(userId),
        fetchActiveSubscription(),
      ]);
      const bankAccounts = companyProfile.bankAccounts || [];
      setCompanyBankAccounts(bankAccounts);
      const budget = getCurrentMonthData(companyProfile.budget || []);
      setBudget(budget);
      setUser(companyProfile);
      setCompany(companyProfile);
      setSubscriptionData(activeSubscription);

      // Determine modal visibility based on fetched data
      determineModalVisibility(companyProfile, activeSubscription, userId);
      return companyProfile;
    } catch (err: any) {
      setError((err?.message || "An unknown error occurred").toString());
    } finally {
      setLoading(false);
    }
  };
  return {
    fetchData,
    isPasswordModalShown,
    error,
    businessModalVisible,
    showFeatures,
    loading,
    setBusinessModalVisible,
    setIsPasswordModalShown,
  };
};
